/**
 * useModelsCatalog — fetches the LLM catalogue once per app and exposes it
 * through a tiny reactive hook.
 *
 * The fetch is initiated EAGERLY at module load (singleton promise) so that
 * by the time any component subscribes, the catalogue is either ready or
 * a single in-flight request is shared. This avoids:
 *   - Per-component redundant fetches
 *   - The `[state.status]` re-run pitfall that aborted the in-flight call
 *     (previous bug — the cleanup of the first render aborted the fetch
 *     before the second render could observe the result).
 */
import { useEffect, useState } from "react";
import type { ModelCatalogEntry } from "../domain/types";
import { fetchModelsCatalog, getCachedCatalog } from "../transport/modelsClient";

type Status = "loading" | "ready" | "error";

interface State {
  models: ModelCatalogEntry[];
  defaultId: string | null;
  status: Status;
  error: string | null;
}

// --- Module-level singleton: one fetch per app, shared across subscribers.

type Listener = (s: State) => void;

let _state: State = (() => {
  const cached = getCachedCatalog();
  if (cached) {
    return {
      models: cached.models,
      defaultId: cached.default,
      status: "ready" as const,
      error: null,
    };
  }
  return { models: [], defaultId: null, status: "loading" as const, error: null };
})();

const _listeners = new Set<Listener>();

function _setState(next: State): void {
  _state = next;
  for (const l of _listeners) l(next);
}

let _started = false;
function _ensureStarted(): void {
  if (_started) return;
  _started = true;
  if (_state.status === "ready") return;
  fetchModelsCatalog()
    .then((c) =>
      _setState({
        models: c.models,
        defaultId: c.default,
        status: "ready",
        error: null,
      }),
    )
    .catch((e) => {
      _setState({
        models: [],
        defaultId: null,
        status: "error",
        error: (e as Error)?.message ?? String(e),
      });
    });
}

// Kick off as soon as this module is imported in the browser.
if (typeof window !== "undefined") {
  _ensureStarted();
}

export function useModelsCatalog(): State {
  const [snap, setSnap] = useState<State>(_state);

  useEffect(() => {
    _ensureStarted();
    const listener: Listener = (s) => setSnap(s);
    _listeners.add(listener);
    // Sync once in case the singleton already changed between render & mount.
    setSnap(_state);
    return () => {
      _listeners.delete(listener);
    };
  }, []);

  return snap;
}
